/**
 * CaptureFormsTable — admin CRUD table for capture forms.
 *
 * Mounted under the "Capture Forms" tab in platform settings.
 * Supports create, edit, and delete of AdminCaptureForm records via the
 * capture-forms/admin/forms/ API endpoint. A form owns a list of bindings,
 * each of which attaches the form to a context (stage-gated / manual /
 * activity-gated) and carries its trigger.
 */
import { ColumnDef } from '@tanstack/react-table'
import to from 'await-to-js'
import { useMemo, useState } from 'react'
import { Alert } from 'components/FbUI/Alert'
import { Skeleton } from 'components/FbUI/Skeleton'
import {
  Control,
  Controller,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form'
import {
  PiEye,
  PiMagnifyingGlass,
  PiMinus,
  PiPlus,
  PiTrash,
} from 'react-icons/pi'
import { useQuery } from '@tanstack/react-query'
import useApiService from 'services/api'
import {
  ActionsDropdown,
  createDeactivateAction,
  createEditAction,
} from 'components/Buttons/ActionsDropdown'
import dialogConfirm from 'components/dialogConfirm'
import { Ds2026ButtonProvider } from 'components/FbUI/Ds2026ButtonContext'
import FbButton from 'components/FbUI/FbButton'
import { BootstrapInput } from 'components/FormUtils/NumericInput'
import { DataTableContainer } from 'components/DataTable/UI'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import { Modal } from 'components/UI/Modal/Modal'
import { cn } from 'components/UI/cn'
import { HelpTooltip } from 'components/Tooltip/HelpTooltip'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { handleFormError } from 'utils/handleFormError'
import { slugify } from 'utils/slugify'
import { ClientDataTable } from '../ClientDataTable/ClientDataTable'
import { CaptureFormRenderer } from './CaptureFormRenderer'
import type {
  AdminCaptureForm,
  AdminCaptureFormInput,
  CaptureFieldDefinition,
  CaptureFieldDefinitionInput,
  CaptureFieldOption,
  CaptureFieldType,
  CaptureTrigger,
} from 'models/captureForms'
import {
  useCreateAdminCaptureForm,
  useDeleteAdminCaptureForm,
  useGetAdminCaptureForms,
  useUpdateAdminCaptureForm,
} from 'services/captureForms'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type FormFieldRow = CaptureFieldDefinitionInput

type BindingRow = {
  trigger: CaptureTrigger
  target_entity: 'deal' | 'company' | null
  gating_stages: number[]
  activity_type: number | null
  order: number
}

type CaptureFormValues = {
  label: string
  key: string
  blocking: boolean
  notification_email: string
  notify_cc_submitter: boolean
  allow_edits: boolean
  is_active: boolean
  // Reserved for the Xceed line phase; carried through, not edited in the UI.
  requires_quantity: boolean
  creates_deals: boolean
  /** Header fields (one value per submission). */
  fields: FormFieldRow[]
  /** Line fields — the template for each repeatable line-item row. */
  lineFields: FormFieldRow[]
  bindings: BindingRow[]
}

const FIELD_TYPES: { value: CaptureFieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency ($)' },
  { value: 'date', label: 'Date' },
  { value: 'notes', label: 'Notes' },
  { value: 'single_select', label: 'Single Select' },
  { value: 'product_variant', label: 'Product Variant' },
]

const TRIGGER_LABELS: Record<CaptureTrigger, string> = {
  manual: 'Manual',
  stage_gated: 'Stage Gated',
  activity_gated: 'Activity Gated',
}

const DEFAULT_FIELD: FormFieldRow = {
  key: '',
  label: '',
  field_type: 'text',
  target_kind: 'header',
  required: false,
  order: 0,
  options: [],
  allow_multiple: false,
}

const DEFAULT_BINDING: BindingRow = {
  trigger: 'manual',
  target_entity: 'company',
  gating_stages: [],
  activity_type: null,
  order: 0,
}

const DEFAULT_VALUES: CaptureFormValues = {
  label: '',
  key: '',
  blocking: false,
  notification_email: '',
  notify_cc_submitter: false,
  allow_edits: true,
  is_active: true,
  requires_quantity: false,
  creates_deals: false,
  fields: [],
  lineFields: [],
  bindings: [],
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Project an API field definition into the builder's editable row shape. */
function projectField(f: CaptureFieldDefinition): FormFieldRow {
  return {
    key: f.key,
    label: f.label,
    field_type: f.field_type,
    target_kind: f.target_kind,
    required: f.required,
    order: f.order,
    options: f.options,
    allow_multiple: f.allow_multiple,
  }
}

function formToFormValues(form: AdminCaptureForm): CaptureFormValues {
  return {
    label: form.label,
    key: form.key,
    blocking: form.blocking,
    notification_email: form.notification_email ?? '',
    notify_cc_submitter: form.notify_cc_submitter,
    allow_edits: form.allow_edits,
    is_active: form.is_active,
    requires_quantity: form.requires_quantity,
    creates_deals: form.creates_deals,
    fields: form.fields
      .filter((f) => f.target_kind !== 'line')
      .map(projectField),
    lineFields: form.fields
      .filter((f) => f.target_kind === 'line')
      .map(projectField),
    bindings: form.bindings.map((b) => ({
      trigger: b.trigger,
      target_entity: b.target_entity,
      gating_stages: b.gating_stages,
      activity_type: b.activity_type,
      order: b.order,
    })),
  }
}

function formValuesToInput(values: CaptureFormValues): AdminCaptureFormInput {
  return {
    key: values.key,
    label: values.label,
    blocking: values.blocking,
    notification_email: values.notification_email || null,
    notify_cc_submitter: values.notify_cc_submitter,
    allow_edits: values.allow_edits,
    requires_quantity: values.requires_quantity,
    creates_deals: values.creates_deals,
    is_active: values.is_active,
    // Header fields then line fields, each tagged with its target_kind and
    // ordered within its group. Line fields form the repeatable-row template.
    fields: [
      ...values.fields.map((f, idx) => ({
        ...f,
        target_kind: 'header' as const,
        order: idx,
      })),
      ...values.lineFields.map((f, idx) => ({
        ...f,
        target_kind: 'line' as const,
        order: idx,
      })),
    ],
    // Normalise each binding to only the context fields its trigger uses, so
    // stale gating_stages / target_entity / activity_type don't leak through.
    bindings: values.bindings.map((b, idx) => ({
      trigger: b.trigger,
      // A manual binding requires a target_entity (backend-enforced). The editor
      // shows "Company" as the default, but that display default isn't written
      // to form state until the user changes the dropdown — so coerce a null
      // target_entity to 'company' here (e.g. switching an activity_gated form,
      // whose target_entity was null, to manual without touching the select).
      target_entity:
        b.trigger === 'activity_gated'
          ? null
          : b.trigger === 'manual'
            ? (b.target_entity ?? 'company')
            : b.target_entity,
      gating_stages: b.trigger === 'stage_gated' ? b.gating_stages : [],
      activity_type: b.trigger === 'activity_gated' ? b.activity_type : null,
      order: idx,
    })),
  }
}

// ---------------------------------------------------------------------------
// Options sub-editor (for single_select fields)
// ---------------------------------------------------------------------------

function OptionsEditor({
  options,
  onChange,
}: {
  options: CaptureFieldOption[]
  onChange: (opts: CaptureFieldOption[]) => void
}) {
  function addOption() {
    onChange([...options, { value: '', label: '' }])
  }

  function removeOption(idx: number) {
    onChange(options.filter((_, i) => i !== idx))
  }

  function updateOption(
    idx: number,
    field: keyof CaptureFieldOption,
    value: string
  ) {
    const next = options.map((o, i) =>
      i === idx ? { ...o, [field]: value } : o
    )
    onChange(next)
  }

  return (
    <div className="ml-4 mt-2 border-l-2 border-line pl-4">
      <p className="mb-1 text-xs font-semibold text-fg-secondary uppercase tracking-wide">
        Options
      </p>
      {options.map((opt, idx) => (
        <div key={idx} className="mb-2 flex items-center gap-2">
          <input
            type="text"
            aria-label={`Option ${idx + 1} value`}
            className="h-8 flex-1 rounded border border-line-form px-2 text-sm text-fg bg-surface focus:outline-none focus:border-brand-active"
            placeholder="Value (slug)"
            value={opt.value}
            onChange={(e) => updateOption(idx, 'value', e.target.value)}
          />
          <input
            type="text"
            aria-label={`Option ${idx + 1} label`}
            className="h-8 flex-1 rounded border border-line-form px-2 text-sm text-fg bg-surface focus:outline-none focus:border-brand-active"
            placeholder="Label"
            value={opt.label}
            onChange={(e) => updateOption(idx, 'label', e.target.value)}
          />
          <button
            type="button"
            onClick={() => removeOption(idx)}
            className="text-fg-tertiary hover:text-error"
            aria-label={`Remove option ${idx + 1}`}
          >
            <PiMinus size={14} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addOption}
        className="flex items-center gap-1 text-xs text-brand-active hover:text-brand"
      >
        <PiPlus size={12} />
        Add option
      </button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Binding row editor
// ---------------------------------------------------------------------------

/**
 * Renders a HelpTooltip for a builder field by its server-side tooltip id.
 * Self-contained (reads preferences) so it can drop in next to any label
 * without threading the tooltips map. Renders nothing if the id has no copy.
 */
function FieldHelp({ id }: { id: string }) {
  const { preferences } = usePreferences()
  const tip = preferences?.tooltips?.[id]
  if (!tip) return null
  return <HelpTooltip title={tip.title} content={tip.content} placement="top" />
}

function BindingRowEditor({
  index,
  control,
  stages,
  onRemove,
}: {
  index: number
  control: Control<CaptureFormValues, any>
  stages: { id: number; stage: string }[]
  onRemove: () => void
}) {
  const trigger = useWatch({
    control,
    name: `bindings.${index}.trigger`,
  }) as CaptureTrigger

  return (
    <div className="mb-3 rounded-md border border-line bg-surface-sunken p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold text-fg-secondary">
          Trigger {index + 1}
        </span>
        <button
          type="button"
          onClick={onRemove}
          className="text-fg-tertiary hover:text-error"
          aria-label="Remove trigger"
        >
          <PiTrash size={14} />
        </button>
      </div>

      <div className="mb-2">
        <div className="mb-1 flex items-center gap-1">
          <label
            htmlFor={`binding-trigger-${index}`}
            className="block text-sm font-medium text-fg"
          >
            Trigger
          </label>
          <FieldHelp id="captureforms_trigger" />
        </div>
        <Controller
          control={control}
          name={`bindings.${index}.trigger`}
          render={({ field }) => (
            <select
              {...field}
              id={`binding-trigger-${index}`}
              className="h-9 w-full rounded border border-line-form bg-surface px-2 text-sm text-fg focus:outline-none focus:border-brand-active"
            >
              <option value="manual">Manual (action menu)</option>
              <option value="stage_gated">Stage Gated (deal)</option>
              <option value="activity_gated">Activity Gated</option>
            </select>
          )}
        />
      </div>

      {trigger === 'manual' && (
        <div className="mb-1">
          <label
            htmlFor={`binding-target-${index}`}
            className="mb-1 block text-sm font-medium text-fg"
          >
            Target Entity
          </label>
          <Controller
            control={control}
            name={`bindings.${index}.target_entity`}
            render={({ field }) => (
              <select
                value={field.value ?? 'company'}
                onChange={(e) => field.onChange(e.target.value)}
                id={`binding-target-${index}`}
                className="h-9 w-full rounded border border-line-form bg-surface px-2 text-sm text-fg focus:outline-none focus:border-brand-active"
              >
                <option value="company">Company</option>
                <option value="deal">Deal</option>
              </select>
            )}
          />
        </div>
      )}

      {trigger === 'stage_gated' && (
        <div className="mb-1">
          <div className="mb-1 flex items-center gap-1">
            <p className="text-sm font-medium text-fg">Gating Stages</p>
            <FieldHelp id="captureforms_gating_stages" />
          </div>
          {stages.length === 0 ? (
            <p className="text-sm text-fg-secondary">
              No stages found. Create sales stages first.
            </p>
          ) : (
            <Controller
              control={control}
              name={`bindings.${index}.gating_stages`}
              render={({ field }) => {
                const selected = (field.value as number[]) ?? []
                function toggle(id: number) {
                  const next = selected.includes(id)
                    ? selected.filter((s) => s !== id)
                    : [...selected, id]
                  field.onChange(next)
                }
                return (
                  <div className="grid grid-cols-2 gap-2 rounded-md border border-line bg-surface p-3">
                    {stages.map((stage) => (
                      <label
                        key={stage.id}
                        htmlFor={`binding-${index}-stage-${stage.id}`}
                        className="flex cursor-pointer select-none items-center gap-2 text-sm text-fg"
                      >
                        <input
                          type="checkbox"
                          id={`binding-${index}-stage-${stage.id}`}
                          checked={selected.includes(stage.id)}
                          onChange={() => toggle(stage.id)}
                          className="h-4 w-4 cursor-pointer accent-brand-active"
                        />
                        {stage.stage}
                      </label>
                    ))}
                  </div>
                )
              }}
            />
          )}
        </div>
      )}

      {trigger === 'activity_gated' && (
        <div className="mb-1">
          <label
            htmlFor={`binding-activity-${index}`}
            className="mb-1 block text-sm font-medium text-fg"
          >
            Activity Type ID
          </label>
          <Controller
            control={control}
            name={`bindings.${index}.activity_type`}
            render={({ field }) => (
              <input
                type="number"
                id={`binding-activity-${index}`}
                className="h-9 w-full rounded border border-line-form bg-surface px-2 text-sm text-fg focus:outline-none focus:border-brand-active"
                placeholder="Activity type id"
                value={field.value ?? ''}
                onChange={(e) =>
                  field.onChange(
                    e.target.value === '' ? null : Number(e.target.value)
                  )
                }
              />
            )}
          />
          <p className="mt-1 text-xs text-fg-secondary">
            The activity type whose completion requires this form.
          </p>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Field row editor
// ---------------------------------------------------------------------------

function FieldRowEditor({
  index,
  control,
  onRemove,
  onFieldTypeChange,
  arrayName = 'fields',
}: {
  index: number

  control: Control<CaptureFormValues, any>
  onRemove: () => void
  /** Called when field_type changes so the parent can clear stale options. */
  onFieldTypeChange: (newType: CaptureFieldType) => void
  /** Which field array this row belongs to (header 'fields' vs 'lineFields'). */
  arrayName?: 'fields' | 'lineFields'
}) {
  const fieldType = useWatch({
    control,
    name: `${arrayName}.${index}.field_type`,
  }) as CaptureFieldType

  return (
    <div className="mb-3 rounded-md border border-line bg-surface-sunken p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold text-fg-secondary">
          Field {index + 1}
        </span>
        <button
          type="button"
          onClick={onRemove}
          className="text-fg-tertiary hover:text-error"
          aria-label="Remove field"
        >
          <PiTrash size={14} />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <BootstrapInput
          name={`${arrayName}.${index}.key`}
          control={control}
          label="Key (slug)"
          placeholder="e.g. close_reason"
        />
        <BootstrapInput
          name={`${arrayName}.${index}.label`}
          control={control}
          label="Label"
          placeholder="e.g. Close Reason"
        />
      </div>
      <div className="grid grid-cols-2 items-end gap-2">
        <div className="mb-2">
          <label
            htmlFor={`field-type-${index}`}
            className="mb-1 block text-sm font-medium text-fg"
          >
            Field Type
          </label>
          <Controller
            control={control}
            name={`${arrayName}.${index}.field_type`}
            render={({ field }) => (
              <select
                {...field}
                id={`field-type-${index}`}
                className="h-9 w-full rounded border border-line-form bg-surface px-2 text-sm text-fg focus:outline-none focus:border-brand-active"
                onChange={(e) => {
                  const next = e.target.value as CaptureFieldType
                  field.onChange(next)
                  // Clearing options when switching away from single_select is
                  // done by the parent via update() — avoids a render-phase side effect.
                  onFieldTypeChange(next)
                }}
              >
                {FIELD_TYPES.map((ft) => (
                  <option key={ft.value} value={ft.value}>
                    {ft.label}
                  </option>
                ))}
              </select>
            )}
          />
        </div>
        <div className="mb-2 flex items-center gap-2 pb-3">
          <Controller
            control={control}
            name={`${arrayName}.${index}.required`}
            render={({ field }) => (
              <label
                htmlFor={`field-required-${index}`}
                className="flex cursor-pointer select-none items-center gap-2 text-sm text-fg"
              >
                <input
                  type="checkbox"
                  id={`field-required-${index}`}
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  className="h-4 w-4 cursor-pointer accent-brand-active"
                />
                Required
              </label>
            )}
          />
        </div>
      </div>
      {fieldType === 'single_select' && (
        <Controller
          control={control}
          name={`${arrayName}.${index}.options`}
          render={({ field }) => (
            <OptionsEditor
              options={field.value as CaptureFieldOption[]}
              onChange={field.onChange}
            />
          )}
        />
      )}
      {fieldType === 'product_variant' && (
        <Controller
          control={control}
          name={`${arrayName}.${index}.allow_multiple`}
          render={({ field }) => (
            <label
              htmlFor={`field-allow-multiple-${index}`}
              className="flex cursor-pointer select-none items-center gap-2 text-sm text-fg"
            >
              <input
                type="checkbox"
                id={`field-allow-multiple-${index}`}
                checked={!!field.value}
                onChange={(e) => field.onChange(e.target.checked)}
                className="h-4 w-4 cursor-pointer accent-brand-active"
              />
              Allow selecting multiple variants
            </label>
          )}
        />
      )}
    </div>
  )
}

/**
 * A titled list of field editors backed by one field array (header or line).
 * Both builder sections share this: only the copy, wrapper, and which field
 * array they drive differ.
 */
function FieldEditorSection({
  title,
  addLabel,
  emptyText,
  description,
  className = 'mb-2',
  arrayName,
  control,
  rows,
  onAppend,
  onRemove,
  onUpdate,
}: {
  title: string
  addLabel: string
  emptyText?: string
  description?: string
  className?: string
  arrayName: 'fields' | 'lineFields'
  control: Control<CaptureFormValues, any>
  rows: (FormFieldRow & { id: string })[]
  onAppend: () => void
  onRemove: (index: number) => void
  onUpdate: (index: number, value: FormFieldRow) => void
}) {
  return (
    <div className={className}>
      <div className="mb-1 flex items-center justify-between">
        <p className="text-sm font-semibold text-fg">{title}</p>
        <button
          type="button"
          onClick={onAppend}
          className="flex items-center gap-1 text-xs text-brand-active hover:text-brand"
        >
          <PiPlus size={12} />
          {addLabel}
        </button>
      </div>
      {description && (
        <p className="mb-2 text-xs text-fg-secondary">{description}</p>
      )}
      {rows.length === 0 && emptyText && (
        <p className="text-sm text-fg-secondary">{emptyText}</p>
      )}
      {rows.map((row, idx) => (
        <FieldRowEditor
          key={row.id}
          index={idx}
          control={control}
          arrayName={arrayName}
          onRemove={() => onRemove(idx)}
          onFieldTypeChange={(newType) => {
            // When type changes away from single_select, clear stale options so
            // they aren't submitted to the API.
            if (newType !== 'single_select') {
              onUpdate(idx, { ...row, field_type: newType, options: [] })
            }
          }}
        />
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Editor modal
// ---------------------------------------------------------------------------

interface CaptureFormEditorModalProps {
  open: boolean
  selectedForm: AdminCaptureForm | null
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

function CaptureFormEditorModal({
  open,
  selectedForm,
  onOpenChange,
  onSuccess,
}: CaptureFormEditorModalProps) {
  const api = useApiService()
  const createMutation = useCreateAdminCaptureForm()
  const updateMutation = useUpdateAdminCaptureForm()

  const { data: stages } = useQuery({
    queryKey: ['stages'],
    queryFn: () => api.getSalesStages(),
  })
  const stageList = stages ?? []

  // Tooltip copy is managed server-side (preferences fixtures), never hardcoded.
  const { preferences } = usePreferences()
  const tooltips: Record<string, { title?: string; content?: string }> =
    preferences?.tooltips ?? {}

  // Whether the admin has typed in the Key field directly. Once they have, we
  // stop auto-deriving it from the Form Name. Resets on each open (the modal
  // remounts via its `key` prop).
  const [keyEdited, setKeyEdited] = useState(false)

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, errors },
    setError,
    setValue,
    reset,
  } = useForm<CaptureFormValues>({
    // The modal remounts per form (key={selectedForm?.id ?? 'new'} at the call
    // site), so initial values are derived here from the selected form.
    // Editing => prefill the form with the existing config; creating => blanks.
    defaultValues: selectedForm
      ? formToFormValues(selectedForm)
      : DEFAULT_VALUES,
    mode: 'onSubmit',
  })

  const {
    fields: fieldRows,
    append,
    remove,
    update,
  } = useFieldArray({
    control,
    name: 'fields',
  })

  const {
    fields: lineFieldRows,
    append: appendLineField,
    remove: removeLineField,
    update: updateLineField,
  } = useFieldArray({
    control,
    name: 'lineFields',
  })

  const {
    fields: bindingRows,
    append: appendBinding,
    remove: removeBinding,
  } = useFieldArray({
    control,
    name: 'bindings',
  })

  const onSubmitCreate = handleSubmit(async (values) => {
    const [err] = await to(
      createMutation.mutateAsync(formValuesToInput(values))
    )
    if (err) {
      handleFormError(err, setError)
      return
    }
    onSuccess()
    onOpenChange(false)
    reset(DEFAULT_VALUES)
  })

  const onSubmitUpdate = handleSubmit(async (values) => {
    if (!selectedForm) return
    const [err] = await to(
      updateMutation.mutateAsync({
        id: selectedForm.id,
        data: formValuesToInput(values),
      })
    )
    if (err) {
      handleFormError(err, setError)
      return
    }
    onSuccess()
    onOpenChange(false)
    reset(DEFAULT_VALUES)
  })

  function handleOpenChange(next: boolean) {
    if (!next) {
      reset(DEFAULT_VALUES)
    } else if (selectedForm) {
      reset(formToFormValues(selectedForm))
    } else {
      reset(DEFAULT_VALUES)
    }
    onOpenChange(next)
  }

  const isEditing = !!selectedForm

  return (
    <Ds2026ButtonProvider>
      <Modal
        open={open}
        title={isEditing ? 'Edit Capture Form' : 'New Capture Form'}
        description={
          isEditing
            ? 'Update the configuration for this capture form.'
            : 'Configure a new capture form that appears at key workflow moments.'
        }
        onOpenChange={handleOpenChange}
        loading={isSubmitting}
        onAccept={isEditing ? onSubmitUpdate : onSubmitCreate}
        acceptButtonText={isEditing ? 'Update' : 'Create'}
        size="lg"
      >
        {errors.root?.message && (
          <Alert color="danger" description={errors.root.message} />
        )}

        {/* Top-level fields */}
        <div className="grid grid-cols-2 gap-x-3">
          <BootstrapInput
            name="label"
            control={control}
            label="Form Name"
            placeholder="e.g. Win/Loss Reasons"
            onValueChange={(v) => {
              // Auto-derive the key from the name while creating, until the
              // admin edits the key themselves.
              if (!selectedForm && !keyEdited) {
                setValue('key', slugify(v))
              }
            }}
          />
          <BootstrapInput
            name="key"
            control={control}
            label="Key (slug)"
            placeholder="e.g. win_loss_reasons"
            onValueChange={() => setKeyEdited(true)}
            tooltip={<FieldHelp id="captureforms_key" />}
          />
        </div>

        <BootstrapInput
          name="notification_email"
          control={control}
          label="Notification Email (optional)"
          placeholder="e.g. manager@company.com"
          tooltip={<FieldHelp id="captureforms_notification_email" />}
        />

        {/* Checkboxes row */}
        <div className="mb-3 flex flex-wrap items-center gap-5">
          {(
            [
              {
                name: 'blocking',
                label: 'Blocking',
                tooltipId: 'captureforms_blocking',
              },
              {
                name: 'notify_cc_submitter',
                label: 'CC submitter on notifications',
                tooltipId: 'captureforms_notify_cc_submitter',
              },
              {
                name: 'allow_edits',
                label: 'Allow editing after submission',
                tooltipId: 'captureforms_allow_edits',
              },
              {
                name: 'is_active',
                label: 'Active',
                tooltipId: 'captureforms_active',
              },
            ] as const
          ).map(({ name, label, tooltipId }) => {
            const tip = tooltips[tooltipId]
            return (
              <div key={name} className="flex items-center gap-1.5">
                <Controller
                  control={control}
                  name={name}
                  render={({ field }) => (
                    <label
                      htmlFor={`chk-${name}`}
                      className="flex cursor-pointer select-none items-center gap-2 text-sm text-fg"
                    >
                      <input
                        type="checkbox"
                        id={`chk-${name}`}
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        className="h-4 w-4 cursor-pointer accent-brand-active"
                      />
                      {label}
                    </label>
                  )}
                />
                {tip ? (
                  <HelpTooltip
                    title={tip.title}
                    content={tip.content}
                    placement="top"
                  />
                ) : null}
              </div>
            )
          })}
        </div>

        {/* Bindings — where/how this form is triggered */}
        <div className="mb-3">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-semibold text-fg">Triggers</p>
            <button
              type="button"
              onClick={() =>
                appendBinding({
                  ...DEFAULT_BINDING,
                  order: bindingRows.length,
                })
              }
              className="flex items-center gap-1 text-xs text-brand-active hover:text-brand"
            >
              <PiPlus size={12} />
              Add trigger
            </button>
          </div>
          {bindingRows.length === 0 && (
            <p className="text-sm text-fg-secondary">
              No triggers yet. Add at least one so the form appears somewhere.
            </p>
          )}
          {bindingRows.map((row, idx) => (
            <BindingRowEditor
              key={row.id}
              index={idx}
              control={control}
              stages={stageList}
              onRemove={() => removeBinding(idx)}
            />
          ))}
        </div>

        {/* Header fields — one value per submission */}
        <FieldEditorSection
          title="Form Fields"
          addLabel="Add field"
          emptyText="No header fields yet."
          arrayName="fields"
          control={control}
          rows={fieldRows}
          onAppend={() => append({ ...DEFAULT_FIELD, order: fieldRows.length })}
          onRemove={remove}
          onUpdate={update}
        />

        {/* Line fields — the template for each repeatable row a rep can add
            (e.g. a product row on a call report). */}
        <FieldEditorSection
          title="Line Items (repeatable rows)"
          addLabel="Add line field"
          description="Fields here become the columns of a row a rep can add one or more of per submission. Leave empty for a single, non-repeating form."
          className="mb-2 rounded-md border border-dashed border-line p-3"
          arrayName="lineFields"
          control={control}
          rows={lineFieldRows}
          onAppend={() =>
            appendLineField({
              ...DEFAULT_FIELD,
              target_kind: 'line',
              order: lineFieldRows.length,
            })
          }
          onRemove={removeLineField}
          onUpdate={updateLineField}
        />
      </Modal>
    </Ds2026ButtonProvider>
  )
}

// ---------------------------------------------------------------------------
// Main table component
// ---------------------------------------------------------------------------

export function CaptureFormsTable() {
  const { data, isLoading } = useGetAdminCaptureForms()
  const deleteAdminCaptureForm = useDeleteAdminCaptureForm()

  const [showModal, setShowModal] = useState(false)
  const [selectedForm, setSelectedForm] = useState<AdminCaptureForm | null>(
    null
  )
  const [previewForm, setPreviewForm] = useState<AdminCaptureForm | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const filteredData = useMemo(() => {
    if (!data) return []
    if (!searchQuery) return data
    const q = searchQuery.toLowerCase()
    return data.filter(
      (form) =>
        form.label.toLowerCase().includes(q) ||
        form.key.toLowerCase().includes(q)
    )
  }, [data, searchQuery])

  function openCreate() {
    setSelectedForm(null)
    setShowModal(true)
  }

  function openEdit(form: AdminCaptureForm) {
    setSelectedForm(form)
    setShowModal(true)
  }

  function handleRowEdit(index: number) {
    const form = filteredData[index]
    if (!form) return
    openEdit(form)
  }

  function handleRowPreview(index: number) {
    const form = filteredData[index]
    if (!form) return
    setPreviewForm(form)
  }

  async function handleRowDelete(index: number) {
    const form = filteredData[index]
    if (!form) return
    if (
      await dialogConfirm(
        `Are you sure you want to deactivate "${form.label}"?`,
        'Deactivate Capture Form'
      )
    ) {
      try {
        await deleteAdminCaptureForm.mutateAsync(form.id)
      } catch {
        // Error toast handled by baseAPICall
      }
    }
  }

  const columns = useMemo<ColumnDef<AdminCaptureForm, any>[]>(
    () => [
      {
        accessorKey: 'label',
        header: 'Form Name',
        size: 220,
        meta: { isFirstColumn: true },
      },
      {
        accessorKey: 'key',
        header: 'Key',
        size: 180,
      },
      {
        id: 'target',
        header: 'Target',
        size: 110,
        accessorFn: (row) => {
          const targets = [
            ...new Set(
              row.bindings
                .map((b) => b.target_entity)
                .filter((t): t is 'deal' | 'company' => !!t)
            ),
          ].map((t) => (t === 'deal' ? 'Deal' : 'Company'))
          return targets.length ? targets.join(', ') : '—'
        },
      },
      {
        id: 'triggers',
        header: 'Triggers',
        size: 200,
        accessorFn: (row) =>
          row.bindings.length === 0
            ? '—'
            : [
                ...new Set(row.bindings.map((b) => TRIGGER_LABELS[b.trigger])),
              ].join(', '),
      },
      {
        id: 'fields_count',
        header: 'Fields',
        size: 80,
        accessorFn: (row) => row.fields.length,
      },
      {
        accessorKey: 'is_active',
        header: 'Active',
        size: 80,
        cell: (info) => {
          const v = info.getValue<boolean>()
          return (
            <span
              className={cn(
                'inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium',
                v
                  ? 'bg-success-bg-subtle text-success-strong'
                  : 'bg-surface-sunken text-fg-secondary'
              )}
            >
              {v ? 'Yes' : 'No'}
            </span>
          )
        },
      },
      {
        id: 'actions',
        header: () => <div className="w-full text-center">Actions</div>,
        size: 70,
        meta: {
          isLastColumn: true,
          headerDisableOrdering: true,
        },
        cell: ({ row }) => (
          <ActionsDropdown
            className="justify-center"
            actions={[
              {
                label: 'Preview',
                icon: PiEye,
                onClick: () => handleRowPreview(row.index),
              },
              createEditAction(() => handleRowEdit(row.index)),
              createDeactivateAction(() => handleRowDelete(row.index)),
            ]}
          />
        ),
      },
    ],
    [filteredData]
  )

  if (isLoading) {
    return (
      <div className="flex w-full flex-col gap-3 p-4">
        <Skeleton className="h-9 w-full max-w-md" />
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-11 w-full" />
        ))}
      </div>
    )
  }

  return (
    <TableLinkStyleProvider variant="plain">
      <div className="flex w-full flex-1 min-h-0 flex-col overflow-hidden rounded-lg border border-line bg-surface shadow-[0px_1px_3px_0px_rgba(16,24,40,0.08),0px_1px_2px_-1px_rgba(16,24,40,0.04)]">
        <DataTableContainer className="h-full">
          <ClientDataTable
            tableHeader={
              <div
                data-table-toolbar
                className="flex flex-row items-center justify-between gap-3 px-4 py-3"
              >
                <div className="relative w-full max-w-md">
                  <PiMagnifyingGlass
                    size={16}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-secondary"
                  />
                  <input
                    type="text"
                    aria-label="Search capture forms"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search forms..."
                    className="h-9 w-full rounded-lg border border-line-form bg-surface pl-9 pr-3 text-sm text-fg placeholder:text-fg-muted focus:border-brand-active focus:outline-none focus:shadow-[0_0_0_4px_rgba(8,116,67,0.2)]"
                  />
                </div>
                <div className="shrink-0">
                  <Ds2026ButtonProvider>
                    <FbButton variant="primary" onClick={openCreate}>
                      <PiPlus size={16} />
                      New Form
                    </FbButton>
                  </Ds2026ButtonProvider>
                </div>
              </div>
            }
            height="100%"
            stickyLastColumn
            data={filteredData}
            columns={columns}
            enableColumnResizing
            rowHeight={44}
            headerHeight={40}
            tableKey="settings-capture-forms-table"
            emptyContent={
              searchQuery
                ? 'No capture forms match your search.'
                : 'No capture forms yet. Create one to collect structured data at key moments (deal close, change in demand).'
            }
          />
          <CaptureFormEditorModal
            key={selectedForm?.id ?? 'new'}
            open={showModal}
            selectedForm={selectedForm}
            onOpenChange={(open) => {
              setShowModal(open)
              if (!open) setSelectedForm(null)
            }}
            onSuccess={() => {
              setShowModal(false)
              setSelectedForm(null)
            }}
          />
          {previewForm && (
            <Modal
              open={!!previewForm}
              title={`Preview: ${previewForm.label}`}
              description="This is how the form appears to a rep. Submissions are disabled in preview."
              onOpenChange={(open) => {
                if (!open) setPreviewForm(null)
              }}
              cancelButtonText="Close"
              size="lg"
            >
              <CaptureFormRenderer
                form={previewForm}
                preview
                onSubmit={() => undefined}
              />
            </Modal>
          )}
        </DataTableContainer>
      </div>
    </TableLinkStyleProvider>
  )
}
